import { useState } from "react";
import { Eye, Moon, Sun } from "lucide-react";

import { ButtonShadcn } from "@/components/ui/Button";
import {
  BRANDING_SHELL,
  BRANDING_SHELL_STYLE,
  BRANDING_CARD_PADDING,
  BRANDING_TILE_RADIUS,
  BRANDING_PILL_TRACK,
  BRANDING_PILL_BUTTON,
  BRANDING_PILL_BUTTON_ACTIVE,
  BRANDING_PILL_BUTTON_INACTIVE,
  DARK_TILE_BG,
  DARK_TILE_BORDER,
  DARK_LOGO_BG,
  DARK_LOGO_BORDER,
  DARK_CHROME_BG,
  LIGHT_CHROME_BG,
  DARK_DOT,
  LIGHT_DOT,
  DARK_SKELETON,
  LIGHT_SKELETON,
  LIGHT_TILE_BG,
  LIGHT_LOGO_BG,
} from "../branding.tokens";
import BrandingSectionHeader from "./BrandingSectionHeader";

const getAssetUrl = (draftAssets, publishedAssets, key) =>
  draftAssets?.[key]?.url || publishedAssets?.[key]?.url || null;

/**
 * Fixed colors throughout — do NOT use theme tokens here. This tile is a
 * miniature browser frame simulating a real light/dark surface regardless of
 * the admin panel's own theme, so every color in it (chrome bar, dots,
 * skeleton content) has to hold still even when the admin panel is toggled.
 */
const PreviewTile = ({ theme, values, logoUrl }) => {
  const isDark = theme === "dark";

  const frameStyle = {
    borderColor: isDark ? DARK_TILE_BORDER : "rgba(0,0,0,0.08)",
    borderWidth: 1,
    borderStyle: "solid",
  };

  const chromeStyle = {
    backgroundColor: isDark ? DARK_CHROME_BG : LIGHT_CHROME_BG,
    borderColor: isDark ? DARK_TILE_BORDER : "rgba(0,0,0,0.08)",
  };

  const bodyStyle = { backgroundColor: isDark ? DARK_TILE_BG : LIGHT_TILE_BG };

  const logoStyle = {
    backgroundColor: isDark ? DARK_LOGO_BG : LIGHT_LOGO_BG,
    borderColor: isDark ? DARK_LOGO_BORDER : "rgba(0,0,0,0.08)",
    borderWidth: 1,
    borderStyle: "solid",
  };

  const dotColor = isDark ? DARK_DOT : LIGHT_DOT;
  const skeletonColor = isDark ? DARK_SKELETON : LIGHT_SKELETON;
  const titleColor = isDark ? "#f1f2f4" : "#111827";
  const subColor = isDark ? "#8a929c" : "#6b7280";
  const logoTextColor = isDark ? "#7f8894" : "#6b7280";

  return (
    <div className={`overflow-hidden ${BRANDING_TILE_RADIUS}`} style={frameStyle}>
      <div className="flex items-center gap-1 border-b px-3 py-2" style={chromeStyle}>
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dotColor }} />
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dotColor }} />
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dotColor }} />
        <span className="ml-2 text-[10px] font-medium uppercase tracking-wide" style={{ color: subColor }}>
          {isDark ? "Dark mode" : "Light mode"}
        </span>
      </div>

      <div className="p-5" style={bodyStyle}>
        <div className="flex items-start gap-4">
          <div
            className="flex h-16 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md"
            style={logoStyle}
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Brand logo preview" className="max-h-11 max-w-16 object-contain" />
            ) : (
              <span className="text-[10px] font-medium" style={{ color: logoTextColor }}>
                No logo
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1 pt-1">
            <h4 className="truncate text-base font-semibold leading-5" style={{ color: titleColor }}>
              {values?.displayName || "Tenant Name"}
            </h4>
          </div>
        </div>

        {/* Stand-in page content — sells the header as sitting on a real
            screen instead of floating in a swatch by itself. */}
        <div className="mt-5 space-y-2">
          <div className="h-2 w-3/4 rounded-full" style={{ backgroundColor: skeletonColor }} />
          <div className="h-2 w-1/2 rounded-full" style={{ backgroundColor: skeletonColor }} />
          <div className="h-2 w-2/3 rounded-full" style={{ backgroundColor: skeletonColor }} />
        </div>
      </div>
    </div>
  );
};

const BrandingPreview = ({ values, draftAssets, publishedAssets }) => {
  const [activeTheme, setActiveTheme] = useState("light");

  const logoAsset = getAssetUrl(draftAssets, publishedAssets, "logo");
  const logoDarkAsset = getAssetUrl(draftAssets, publishedAssets, "logo_dark");
  const lightLogoUrl = logoAsset || logoDarkAsset;
  const darkLogoUrl = logoDarkAsset || logoAsset;
  const logoUrl = activeTheme === "dark" ? darkLogoUrl : lightLogoUrl;

  return (
    <div className={`${BRANDING_SHELL} ${BRANDING_CARD_PADDING}`} style={BRANDING_SHELL_STYLE}>
      <BrandingSectionHeader
        icon={Eye}
        title="Preview"
        subtitle="See how your branding appears to users."
        action={
          <div className={BRANDING_PILL_TRACK} role="tablist" aria-label="Preview theme">
            <ButtonShadcn
              variant="ghost"
              role="tab"
              aria-selected={activeTheme === "light"}
              onClick={() => setActiveTheme("light")}
              className={`h-auto ${BRANDING_PILL_BUTTON} ${activeTheme === "light" ? BRANDING_PILL_BUTTON_ACTIVE : BRANDING_PILL_BUTTON_INACTIVE}`}
            >
              <Sun className="h-3 w-3" aria-hidden="true" /> Light
            </ButtonShadcn>
            <ButtonShadcn
              variant="ghost"
              role="tab"
              aria-selected={activeTheme === "dark"}
              onClick={() => setActiveTheme("dark")}
              className={`h-auto ${BRANDING_PILL_BUTTON} ${activeTheme === "dark" ? BRANDING_PILL_BUTTON_ACTIVE : BRANDING_PILL_BUTTON_INACTIVE}`}
            >
              <Moon className="h-3 w-3" aria-hidden="true" /> Dark
            </ButtonShadcn>
          </div>
        }
      />

      <PreviewTile theme={activeTheme} values={values} logoUrl={logoUrl} />

      <p className="mt-2.5 text-[11px] text-text-secondary">Updates automatically as you edit.</p>
    </div>
  );
};

export default BrandingPreview;
